import React, { useState, useEffect } from 'react';
import { X, Heart } from 'lucide-react';

/**
 * Tema del Día de la Madre.
 *
 * - Banner superior con mensaje y botón cerrar (recordado en localStorage)
 * - Animación sutil de pétalos cayendo (overlay sin bloqueo de clics)
 * - Auto-oculta automáticamente despues de MOTHERS_DAY_END_DATE
 *
 * Para desactivarlo manualmente desde código, ajustar MOTHERS_DAY_END_DATE
 * a una fecha anterior a hoy o renderizar este componente solo bajo una condición.
 */

// Fecha final (inclusive el domingo del Día de la Madre).
// Después de esta fecha el tema deja de mostrarse automáticamente.
// Para reactivar el próximo año, cambiar a una fecha futura (ej: '2027-05-09').
const MOTHERS_DAY_END_DATE = '2026-05-10'; // Desactivado tras el Día de la Madre 2026
const DISMISS_STORAGE_KEY = 'mothersDay2026_dismissed';
const PETAL_COUNT = 14;

// Pétalos pre-calculados para evitar recalcular en cada render
const generatePetals = () => {
  const petals = [];
  const colors = ['#fbcfe8', '#f9a8d4', '#fda4af', '#fbb6ce', '#fcd5ce', '#fde68a'];
  for (let i = 0; i < PETAL_COUNT; i++) {
    petals.push({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 12,
      duration: 14 + Math.random() * 10,
      size: 10 + Math.random() * 12,
      color: colors[Math.floor(Math.random() * colors.length)],
      drift: -30 + Math.random() * 60,
      rotateSpeed: 1 + Math.random() * 2,
    });
  }
  return petals;
};

const PETALS = generatePetals();

const isThemeActive = () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(MOTHERS_DAY_END_DATE);
    return today <= end;
  } catch {
    return false;
  }
};

export default function MothersDayTheme() {
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    if (!isThemeActive()) return;
    const dismissed = localStorage.getItem(DISMISS_STORAGE_KEY) === 'true';
    if (!dismissed) setBannerVisible(true);
  }, []);

  // Si el tema ya pasó la fecha, no renderizar nada (ni pétalos ni banner)
  if (!isThemeActive()) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_STORAGE_KEY, 'true');
    setBannerVisible(false);
  };

  return (
    <>
      {/* Banner superior */}
      {bannerVisible && (
        <div
          data-testid="mothers-day-banner"
          className="relative w-full bg-gradient-to-r from-rose-200 via-pink-300 to-amber-200 border-b border-pink-300/60 shadow-sm"
        >
          <div className="container mx-auto px-4 py-2 flex items-center justify-center relative">
            <div className="flex items-center gap-2 text-rose-900">
              <Heart className="h-4 w-4 fill-rose-600 text-rose-600 animate-pulse" />
              <span className="text-sm sm:text-base font-medium tracking-wide">
                Feliz Dia de la Madre &mdash; <span className="font-semibold">SIEMPRE</span> celebra a todas las mamas
              </span>
              <Heart className="h-4 w-4 fill-rose-600 text-rose-600 animate-pulse" />
            </div>
            <button
              data-testid="mothers-day-banner-close"
              onClick={handleDismiss}
              aria-label="Cerrar mensaje del Dia de la Madre"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/40 transition-colors text-rose-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Overlay de pétalos */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
        data-testid="mothers-day-petals"
      >
        {PETALS.map((p) => (
          <span
            key={p.id}
            className="md-petal"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * 1.4}px`,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              '--md-drift': `${p.drift}px`,
              '--md-rotate-speed': `${p.rotateSpeed}`,
            }}
          />
        ))}
      </div>
    </>
  );
}
